import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../features/Cart/useCart";
import { useCartStore } from "../../store/cartStore";
import { toPersianNumbers } from "../../utils/toPersianNumbers";
import QuantitySelector from "./QuantitySelector";

const MiniCart = ({ children, disabled = false }) => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const cartFromStore = useCartStore((state) => state.cart);

  const {
    data: cartFromQuery,
    isLoading,
    isFetching,
    isFetched,
    updateQuantity,
    removeItem,
    validateCart,
  } = useCart({
    enabled: isLoggedIn,
  });

  const cart = cartFromStore ?? cartFromQuery;

  const [isOpen, setIsOpen] = useState(false);
  const [updatingItems, setUpdatingItems] = useState({});

  const hoverTimeoutRef = useRef(null);
  const cartRef = useRef(null);

  const items = cart?.items ?? [];
  const itemsCount = cart?.items_count ?? 0;

  const shouldShowLoading =
    isLoggedIn && cart === undefined && (isLoading || isFetching || !isFetched);

  const isCartEmpty =
    isLoggedIn &&
    cart !== undefined &&
    Array.isArray(cart.items) &&
    cart.items.length === 0;

  const handleMouseEnter = () => {
    if (disabled) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (disabled) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    setUpdatingItems((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    try {
      await updateQuantity.mutateAsync({
        itemId,
        quantity: newQuantity,
      });
      validateCart.mutate();
    } catch (error) {
      console.error("Error updating quantity:", error);
    } finally {
      setUpdatingItems((prev) => ({
        ...prev,
        [itemId]: false,
      }));
    }
  };

  const handleRemoveItem = async (itemId) => {
    setUpdatingItems((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    try {
      await removeItem.mutateAsync(itemId);
      validateCart.mutate();
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      setUpdatingItems((prev) => ({
        ...prev,
        [itemId]: false,
      }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={cartRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative cursor-pointer">
        {children}

        {itemsCount > 0 && !disabled && (
          <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {toPersianNumbers(itemsCount)}
          </span>
        )}
      </div>

      <div
        className={`${
          disabled ? "hidden" : isOpen ? "block" : "hidden"
        } absolute left-0 mt-2 w-[420px] bg-white rounded-lg shadow-xl border border-gray-200 z-50`}
      >
        <div className="p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-[#1E3A5F]">سبد خرید</h3>

            {itemsCount > 0 && (
              <span className="text-sm text-gray-600">
                {toPersianNumbers(itemsCount)} کالا
              </span>
            )}
          </div>

          {!isLoggedIn ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                برای مشاهده سبد خرید وارد شوید
              </p>

              <button
                onClick={() => navigate("/user-login")}
                className="bg-[#1E3A5F] text-white px-4 py-2 rounded-lg hover:bg-[#153245] transition-colors"
              >
                ورود به حساب
              </button>
            </div>
          ) : shouldShowLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]" />
            </div>
          ) : isCartEmpty ? (
            <div className="text-center py-8">
              <svg
                className="w-16 h-16 mx-auto text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 014 0z"
                />
              </svg>

              <p className="text-gray-600">سبد خرید شما خالی است</p>
            </div>
          ) : (
            <>
              <div className="max-h-96 overflow-y-auto px-[8px]">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-3 py-3 border-b last:border-0"
                  >
                    <Link
                      to={`/shopping/product-detail/${item.product.slug}`}
                      onClick={() => setIsOpen(false)}
                      className="w-16 h-16 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0"
                    >
                      {item.product?.featured_image_url ? (
                        <img
                          src={item.product.featured_image_url}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg
                            className="w-7 h-7"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                    </Link>

                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/shopping/product-detail/${item.product.slug}`}
                        onClick={() => setIsOpen(false)}
                        className="text-sm font-medium text-gray-800 mb-[8px] hover:text-[#1E3A5F] line-clamp-2"
                      >
                        {item.product.name}
                      </Link>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <QuantitySelector
                            value={item.quantity}
                            onChange={(newQuantity) =>
                              handleUpdateQuantity(item.id, newQuantity)
                            }
                            onRemove={() => handleRemoveItem(item.id)}
                            showRemoveButton={true}
                            min={item.min_order}
                            max={item.max_order}
                            multiple={item.order_multiple}
                            loading={
                              Boolean(updatingItems[item.id]) ||
                              updateQuantity.isPending ||
                              removeItem.isPending
                            }
                            inventoryCount={item.product?.inventory_count}
                            showInventory={false}
                            className="flex-shrink-0"
                            itemId={item.id}
                          />

                          <div className="text-left flex-shrink-0">
                            <div className="text-sm font-semibold text-[#1E3A5F] whitespace-nowrap">
                              {Number(item.final_price).toLocaleString('fa-IR')} تومان
                            </div>

                            {item.discount_amount > 0 && (
                              <div className="text-xs text-gray-400 line-through whitespace-nowrap">
                                {Number(item.subtotal).toLocaleString('fa-IR')} تومان
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-600">جمع کل:</span>

                  <span className="font-semibold text-[#1E3A5F] text-lg">
                    {Number(cart?.final_total || 0).toLocaleString('fa-IR')} تومان
                  </span>
                </div>

                <Link
                  to="/cart"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center bg-[#1E3A5F] text-white py-2.5 rounded-lg hover:bg-[#153245] transition-colors font-medium"
                >
                  مشاهده سبد خرید
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiniCart;
